import { createContext, ReactNode, useContext, useState } from "react";
import { LuX } from "react-icons/lu";

export type SnackbarType = "success" | "error";

export interface SnackbarMessage {
  id: string;
  message: string;
  type: SnackbarType;
  duration?: number | null;
  isVisible?: boolean;
  onClick?: () => void;
}

interface SnackbarProps {
  type?: SnackbarType;
  duration?: number | null;
  onClick?: () => void;
}

interface SnackbarContextType {
  showSnackbar: (message: string, params?: SnackbarProps) => void;
  messages: SnackbarMessage[];
  hideSnackbar: (id: string) => void;
  hideAllSnackbars: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
}

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);

  const showSnackbar = (message: string, options?: SnackbarProps) => {
    const { type = "success", duration = 5000, onClick } = options || {};
    const id = Date.now().toString();
    const newMessage: SnackbarMessage = {
      id,
      message,
      type,
      duration,
      isVisible: false,
      onClick,
    };

    setMessages((prev) => [...prev, newMessage]);

    // Trigger fade in animation
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, isVisible: true } : msg))
      );
    }, 10);

    // Only set up auto-hide if duration is not null
    if (duration !== null) {
      // Start fade out animation before auto-hide
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === id ? { ...msg, isVisible: false } : msg
          )
        );
      }, duration - 300); // Start fade out 300ms before removal

      // Remove from DOM after fade out
      setTimeout(() => {
        hideSnackbar(id);
      }, duration);
    }
  };

  const hideSnackbar = (id: string) => {
    // Start fade out animation
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, isVisible: false } : msg))
    );

    // Remove from DOM after fade out
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    }, 300);
  };

  const hideAllSnackbars = () => {
    setMessages([]);
  };

  const getSnackbarStyles = (type: SnackbarType) => {
    switch (type) {
      case "success":
        return "bg-[#16A34A] text-white";
      case "error":
        return "bg-red-600 text-white";
      default:
        return "bg-blue-600 text-white";
    }
  };

  return (
    <SnackbarContext.Provider
      value={{ showSnackbar, messages, hideSnackbar, hideAllSnackbars }}
    >
      {children}

      {/* Snackbar Container */}
      <div
        className="fixed top-4 
      left-0 right-0
      flex flex-col items-center z-50 gap-2 pointer-events-none"
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${getSnackbarStyles(
              msg.type
            )} px-4 py-2 rounded-md shadow-lg 
            flex items-center justify-center
            gap-2 w-fit mx-4
            transition-opacity duration-300 ease-in-out pointer-events-auto ${
              msg.isVisible ? "opacity-100" : "opacity-0"
            } ${msg.onClick && "cursor-pointer"}`}
            onClick={
              msg.onClick
                ? () => {
                    msg.onClick!();
                    hideSnackbar(msg.id);
                  }
                : undefined
            }
          >
            <span className="text-sm">{msg.message}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                hideSnackbar(msg.id);
              }}
              className="ml-2 hover:bg-white/20 rounded-full p-1 transition-colors duration-200"
              aria-label="Close snackbar"
            >
              <LuX size={16} />
            </button>
          </div>
        ))}
      </div>
    </SnackbarContext.Provider>
  );
}
